import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import EvidenceBadge from "@/components/EvidenceBadge";
import DualScore from "@/components/DualScore";
import CallLog from "@/components/CallLog";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function ProgramDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [matches, setMatches] = useState([]);
  const [ai, setAi] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get(`/programs/${id}`).then(r=>setP(r.data));
    api.get(`/match/program/${id}`).then(r=>setMatches(r.data.matches || []));
  }, [id]);

  const runAI = async () => {
    if (!p) return;
    setAiBusy(true); setAi("");
    try {
      const context = `Program: ${p.name}\nVertical: ${p.vertical}\nAgency: ${p.agency}\nFunder: ${p.funder}\nPopulation: ${p.population}\nGeography: ${(p.geography||[]).join(", ")}\nPayment: ${p.payment_flow} / ${p.payment_amount}\nReferral: ${p.referral_process}\nEvidence: ${p.evidence_level}`;
      const res = await fetch(`${api.defaults.baseURL}/ai/analyze`, {
        method: "POST", credentials: "include",
        headers: {"Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("sahoi_token") || ""}`},
        body: JSON.stringify({ prompt: "Summarize this program's fit for our vetted-property strategy in Greater San Antonio. Identify what evidence is missing to move from assumption to partner-confirmed status. Be concise.", context }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        setAi(prev => prev + dec.decode(value));
      }
    } catch (e) { toast.error("AI analysis failed"); }
    finally { setAiBusy(false); }
  };

  if (!p) return <div>Loading…</div>;
  return (
    <div className="space-y-6">
      <div>
        <Link to="/programs" className="text-xs text-slate-500 hover:text-slate-900">← All Programs</Link>
        <div className="flex justify-between items-start mt-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold capitalize">{p.vertical?.replace("_"," ")}</div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="program-title">{p.name}</h1>
          </div>
          <EvidenceBadge level={p.evidence_level} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <Card className="p-5 md:col-span-2 border-slate-200 space-y-3">
          <Detail label="Agency" value={p.agency} />
          <Detail label="Funder" value={p.funder} />
          <Detail label="Population" value={p.population} />
          <Detail label="Counties" value={(p.geography||[]).join(", ")} />
          <Detail label="Payment Flow" value={p.payment_flow} />
          <Detail label="Payment Amount" value={p.payment_amount} />
          <Detail label="Rent Basis" value={p.rent_basis ? p.rent_basis.replace("_", " ").toUpperCase() : ""} />
          <Detail label="Admin Agent" value={p.admin_agent} />
          <Detail label="Contract Type" value={p.contract_type} />
          <Detail label="Capacity" value={p.capacity} />
          <Detail label="Waitlist Size" value={p.waitlist_size} />
          <Detail label="Avg Length of Stay" value={p.avg_length_of_stay} />
          <Detail label="Referral Process" value={p.referral_process} />
          <Detail label="Property Requirements" value={p.property_requirements} />
          <Detail label="Regulatory" value={p.regulatory} />
          <Detail label="Status" value={p.status} />
          {p.source_url && <Detail label="Source" value={<a className="text-[#C25E00] hover:underline" href={p.source_url} target="_blank" rel="noreferrer">{p.source_url}</a>} />}
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 text-slate-900 font-semibold mb-3"><Sparkles className="w-4 h-4 text-[#C25E00]" /> AI Analyst</div>
          <p className="text-xs text-slate-500 mb-3">Claude Sonnet 5 · Evidence-forward summary and verification gaps.</p>
          <Button onClick={runAI} disabled={aiBusy} className="bg-slate-900 hover:bg-slate-800 w-full" data-testid="ai-analyze-btn">
            {aiBusy ? "Analyzing…" : "Run Analysis"}
          </Button>
          {ai && <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{ai}</div>}
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-3">Agency Call Log</h2>
        <CallLog
          targetType="program"
          targetId={p.id}
          targetLabel={p.name}
          currentEvidenceLevel={p.evidence_level}
          onEvidencePromoted={(lvl) => setP((prev) => prev ? { ...prev, evidence_level: lvl } : prev)}
        />
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-3">Matching Vetted Properties</h2>
        <div className="space-y-3">
          {matches.map(m => (
            <Card key={m.property.id} className="p-4 border-slate-200">
              <div className="flex justify-between items-start gap-6">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/properties/${m.property.id}`} className="font-semibold hover:text-[#C25E00]">{m.property.address}, {m.property.city}</Link>
                    {p.rent_basis && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900 text-white font-semibold uppercase tracking-wider">
                        {p.rent_basis.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{m.property.county} County · {m.property.bedrooms}BR/{m.property.bathrooms}BA · {m.property.property_type}</div>
                  <ul className="text-xs text-emerald-700 mt-2 list-disc list-inside space-y-0.5">
                    {(m.reasons||[]).map((r,i)=><li key={i}>{r}</li>)}
                  </ul>
                  {(m.penalties||[]).length > 0 && <ul className="text-xs text-rose-600 mt-1 list-disc list-inside">{m.penalties.map((r,i)=><li key={i}>{r}</li>)}</ul>}
                </div>
                <div className="w-64 shrink-0"><DualScore opportunity={m.opportunity_score} confidence={m.confidence_score} /></div>
              </div>
            </Card>
          ))}
          {matches.length === 0 && <div className="text-sm text-slate-500">No vetted properties yet. Add some in Properties.</div>}
        </div>
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 text-sm border-b border-slate-100 pb-2">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold col-span-1 pt-1">{label}</div>
    <div className="col-span-2 text-slate-800">{value || <span className="text-slate-400">—</span>}</div>
  </div>
);
